import type { ComponentProps, ReactNode } from "react";
import {
  ButtonRenderProps,
  LinkRenderProps,
  Button as RACButton,
  ButtonProps as RACButtonProps,
  Link as RACLink,
  LinkProps as RACLinkProps,
} from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { focusRing } from "../../utils";
import type { Size } from "../../utils/controlStyles";
import { controlStyles } from "../../utils/controlStyles";
import { Icon } from "../Icon";

type IconName = ComponentProps<typeof Icon>["name"];

type BaseButtonProps = Omit<RACButtonProps, "className">;

/**
 * Button
 *
 * A versatile action component that supports multiple visual variants, sizes,
 * optional icons, a loading state, and badge indicators. Follows the Edges
 * design system tokens and composes `react-aria-components` under the hood.
 *
 * Example usage:
 * ```tsx
 * <Button variant="brand" size="md" icon="Check">Save</Button>
 * ```
 */
export interface ButtonProps extends BaseButtonProps {
  variant?: "default" | "brand" | "secondary" | "destructive" | "icon" | "link" | "unstyled" | "ghost" | "primary";
  size?: Size;
  badgeNumber?: number;
  badgeVariant?: "primary" | "destructive";
  badgePosition?: "top-right" | "top-left" | "bottom-right" | "bottom-left";
  fullWidth?: boolean;
  icon?: IconName;
  iconWeight?: "thin" | "light" | "regular" | "bold" | "fill" | "duotone";
  iconPosition?: "left" | "right";
  isLoading?: boolean;
  loadingText?: string;
  loadingIndicator?: React.ReactNode;
  className?: string;
  href?: string;
  target?: string;
  rel?: string;
  style?: React.CSSProperties;
}

const baseStyles =
  "flex font-medium justify-center items-center gap-2 text-center transition rounded-[var(--control-border-radius)] whitespace-nowrap box-border";

const variantStyles = {
  default:
    "bg-action-default text-[color:var(--color-action-default-text)] hover:bg-action-default-hover forced-colors:bg-[ButtonText] forced-colors:text-[ButtonFace] border-2 border-transparent",
  brand:
    "bg-action-brand text-[color:var(--color-action-brand-text)] hover:bg-action-brand-hover forced-colors:bg-[ButtonText] forced-colors:text-[ButtonFace] border-2 border-transparent",
  secondary:
    "bg-transparent text-text-body border-2 border-border-muted hover:bg-text-body/5 forced-colors:border-[ButtonBorder] forced-colors:bg-[ButtonFace] forced-colors:text-[ButtonText]",
  ghost:
    "border-none text-text-body hover:bg-text-body/5 forced-colors:text-[ButtonText] forced-colors:hover:bg-[ButtonFace]",
  destructive:
    "bg-transparent text-feedback-error-text border-2 border-border-muted hover:bg-feedback-error-background/50 forced-colors:border-[ButtonBorder] forced-colors:bg-[ButtonFace] forced-colors:text-[Mark]",
  link: "!text-action-brand hover:text-action-brand/90 hover:underline hover:decoration-2 hover:underline-offset-4",
  icon: "border-none text-text-body hover:bg-background-hover hover:text-text-heading forced-colors:text-[ButtonText] forced-colors:hover:bg-[ButtonFace] p-1 flex-shrink-0 rounded-[var(--control-border-radius)]",
  unstyled: "bg-transparent",
} as const;

const widthStyles = {
  full: "w-full",
  default: "w-fit",
};

const disabledStyles = "opacity-50 cursor-not-allowed pointer-events-none";
const enabledStyles = "opacity-100 cursor-pointer";

const badgePositionStyles = {
  "top-right": "-right-2 -top-2",
  "top-left": "-left-2 -top-2",
  "bottom-right": "-right-2 -bottom-2",
  "bottom-left": "-left-2 -bottom-2",
} as const;

const badgeVariantStyles = {
  primary: "bg-action-brand text-white",
  destructive: "bg-action-destructive text-white",
} as const;

function normalizeVariant(
  variant: ButtonProps["variant"]
): "default" | "brand" | "secondary" | "destructive" | "icon" | "link" | "unstyled" | "ghost" {
  if (!variant) {
    return "default";
  }

  if (variant === "primary") {
    return "brand";
  }

  return variant;
}

function getButtonStyles(props: ButtonProps) {
  const { variant = "default", size = "md", isDisabled, fullWidth = false, isLoading = false } = props;

  const normalizedVariant = normalizeVariant(variant);

  let styles = normalizedVariant === "unstyled" ? "" : `${baseStyles} ${variantStyles[normalizedVariant]}`;

  // Add size styles using controlStyles
  if (normalizedVariant !== "unstyled") {
    styles += ` ${controlStyles[size].text} ${controlStyles[size].height} ${controlStyles[size].padding} ${controlStyles[size].gap}`;
  }

  if (isDisabled || isLoading) {
    styles += ` ${disabledStyles}`;
  } else {
    styles += ` ${enabledStyles}`;
  }

  if (fullWidth) {
    styles += ` ${widthStyles.full}`;
  } else {
    styles += ` ${widthStyles.default}`;
  }

  const focusRingColor = normalizedVariant === "brand" ? "focus-visible:outline-action-brand" : undefined;

  return [styles, focusRing(), focusRingColor].filter(Boolean).join(" ");
}

/**
 * Renders an Edges Button. When `href` is provided, renders a link-styled
 * button using the same visual system.
 */
export function Button(props: ButtonProps) {
  const {
    icon,
    children,
    isLoading = false,
    loadingText,
    loadingIndicator,
    size = "md",
    iconPosition = "left",
    iconWeight,
    href,
    className,
    badgeNumber,
    badgeVariant = "primary",
    badgePosition = "top-right",
    style,
    target,
    rel,
    ...restProps
  } = props;

  // Shared icon logic
  const iconElement = icon ? <Icon name={icon} size={size} weight={iconWeight} /> : null;

  // Shared content logic
  const content = (
    <>
      {iconPosition === "left" && iconElement}
      {typeof children === "function" ? null : children}
      {iconPosition === "right" && iconElement}
    </>
  );

  // Loading state
  const loadingNode = (
    <div className="relative inline-flex items-center justify-center">
      <div className="absolute inset-0 flex items-center justify-center">
        {loadingIndicator || <Icon name="CircleNotch" size={size} className="animate-spin" aria-hidden="true" />}
      </div>
      <div className="invisible" aria-hidden="true">
        {content}
      </div>
      {loadingText && (
        <span className="sr-only" role="status" aria-live="polite">
          {loadingText}
        </span>
      )}
    </div>
  );

  // Compose className using twMerge to properly handle Tailwind class conflicts
  const hasBadge = (badgeNumber ?? 0) > 0;
  const composedClassName = twMerge(getButtonStyles(props), className);

  // Create link-specific props by filtering out button-specific props
  const linkProps: Partial<RACLinkProps> = href
    ? {
        href,
        target,
        rel,
        // Only include props that are valid for both Button and Link
        ...(restProps as Record<string, unknown>),
      }
    : {};

  // Remove button-specific event handlers that don't apply to links
  if (href) {
    const {
      onPress: _onPress,
      onPressStart: _onPressStart,
      onPressEnd: _onPressEnd,
      onPressChange: _onPressChange,
      onPressUp: _onPressUp,
      onAuxClick: _onAuxClick,
      onContextMenu: _onContextMenu,
      onDoubleClick: _onDoubleClick,
      ...safeRestProps
    } = restProps as Record<string, unknown>;
    Object.assign(linkProps, safeRestProps);
  }

  // Compose the button or link element
  const buttonOrLink = href ? (
    <RACLink
      {...linkProps}
      {...(isLoading && { "aria-busy": true })}
      style={hasBadge ? undefined : style}
      className={composedClassName}
    >
      {isLoading
        ? loadingNode
        : typeof children === "function"
          ? (function (renderProps: LinkRenderProps & { defaultChildren: ReactNode }) {
              return (
                <>
                  {iconPosition === "left" && iconElement}
                  {children(
                    renderProps as unknown as ButtonRenderProps & {
                      defaultChildren: ReactNode;
                    }
                  )}
                  {iconPosition === "right" && iconElement}
                </>
              );
            } as (props: LinkRenderProps & { defaultChildren: ReactNode }) => ReactNode)
          : content}
    </RACLink>
  ) : (
    <RACButton
      {...restProps}
      {...(isLoading && { "aria-busy": true })}
      style={hasBadge ? undefined : style}
      className={composedClassName}
    >
      {isLoading
        ? loadingNode
        : typeof children === "function"
          ? (function (renderProps: ButtonRenderProps & { defaultChildren: ReactNode }) {
              return (
                <>
                  {iconPosition === "left" && iconElement}
                  {children(renderProps)}
                  {iconPosition === "right" && iconElement}
                </>
              );
            } as (props: ButtonRenderProps & { defaultChildren: ReactNode }) => ReactNode)
          : content}
    </RACButton>
  );

  // Render badge if badgeNumber is provided and > 0
  if (hasBadge) {
    return (
      <div className={twMerge("relative flex", className)} style={style}>
        {buttonOrLink}
        <span
          className={`absolute ${badgePositionStyles[badgePosition]} flex h-5 w-5 items-center justify-center rounded-full ${badgeVariantStyles[badgeVariant]} text-xs font-medium`}
          aria-label={`${badgeNumber} items`}
        >
          {badgeNumber}
        </span>
      </div>
    );
  }

  return buttonOrLink;
}
